import { useFormikContext } from 'formik';
import { useCallback, useMemo } from 'react';

import {
  type Resolution,
  type ResolutionConfigItemType,
  REQS_REVIEW_RESOLUTION_TYPES,
} from '@frontend/api';
import {
  VStack,
  DragHandleIcon,
  HStack,
  IconButton,
} from '@frontend/components';

import { ItemAndDropBorderContainer } from 'components/BetweenItemDropBorder';

import { useReqsReviewConfigContext } from '../ReqsReviewConfigContext';
import { createEmptyResolution } from '../utils/resolutions';
import { BetweenResolutionDropBorder } from './BetweenResolutionDropBorder';
import { ResolutionItem } from './ResolutionItem';
import {
  ResolutionTypeSelect,
  IconOption,
  IconControl,
  EmptyGroupHeading,
} from './ResolutionTypeSelect';
import { type FormStateData } from './RulesDetailModal';

type ResolutionsContainerProps = {
  isReadOnly: boolean;
};

export const ResolutionsContainer = ({
  isReadOnly,
}: ResolutionsContainerProps) => {
  const { values, setFieldValue } = useFormikContext<FormStateData>();
  const resolutions = useMemo(
    () => values.resolutions || [],
    [values.resolutions],
  );

  const { resolutionConfig, isLoading } = useReqsReviewConfigContext();

  const handleResolutionChange = useCallback(
    (value: Resolution, index: number) => {
      const resolution = resolutions[index];
      const updatedResolution = {
        ...resolution,
        ...value,
      };

      const newResolutions = [...resolutions];
      newResolutions[index] = updatedResolution;
      setFieldValue('resolutions', newResolutions);
    },
    [resolutions, setFieldValue],
  );

  const resolutionTypeOptions = useMemo(() => {
    if (isLoading) return [];
    return resolutionConfig.display.map((group) => ({
      label: '',
      options: group
        // FIXME: We're temporarily filtering out these two resolution types until we can support them
        .filter(
          (itemType) =>
            itemType !== REQS_REVIEW_RESOLUTION_TYPES.UPDATE_WORKFLOW_STEP &&
            itemType !== REQS_REVIEW_RESOLUTION_TYPES.START_CHAT,
        )
        .map((itemType) => ({
          label: resolutionConfig.mapped[itemType].label,
          value: itemType,
        })),
    }));
  }, [isLoading, resolutionConfig]);

  const handleResolutionTypeChange = useCallback(
    (value: ResolutionConfigItemType, index: number) => {
      if (isLoading || !resolutionConfig.mapped[value]) return;
      const newResolution = createEmptyResolution(
        value,
        resolutionConfig.mapped[value],
      );

      const newResolutions = [...resolutions];
      newResolutions[index] = newResolution;
      setFieldValue('resolutions', newResolutions);
    },
    [isLoading, setFieldValue, resolutionConfig, resolutions],
  );

  const handleClearResolution = useCallback(
    (index: number) => {
      const newResolutions = [...resolutions];
      newResolutions.splice(index, 1);
      setFieldValue('resolutions', newResolutions);
    },
    [resolutions, setFieldValue],
  );

  const handleReorderResolutions = useCallback(
    (oldIndex: number, newIndex: number) => {
      const newResolutions = [...resolutions];
      const [movedResolution] = newResolutions.splice(oldIndex, 1);

      // Calculate the insertion index accounting for the removal
      const insertIndex = newIndex > oldIndex ? newIndex - 1 : newIndex;
      newResolutions.splice(insertIndex, 0, movedResolution);

      setFieldValue('resolutions', newResolutions);
    },
    [resolutions, setFieldValue],
  );

  if (isLoading) return null;

  return (
    <VStack spacing={0} align="stretch" className="resolutions-container">
      {resolutions.map((resolution, index) =>
        // FIXME: We're temporarily filtering out these two resolution types until we can support them,
        // but not from the index data so we can still update at the appropriate index
        resolution.type !== REQS_REVIEW_RESOLUTION_TYPES.UPDATE_WORKFLOW_STEP &&
        resolution.type !== REQS_REVIEW_RESOLUTION_TYPES.START_CHAT ? (
          <ItemAndDropBorderContainer key={resolution.id}>
            <BetweenResolutionDropBorder
              side="top"
              resolutionIndex={index}
              onReorderResolutions={handleReorderResolutions}
            />
            <ResolutionItem
              index={index}
              resolutionConfig={resolutionConfig.mapped[resolution.type]}
              resolutionTypeOptions={resolutionTypeOptions}
              resolution={resolution}
              onClearResolution={handleClearResolution}
              onChangeResolution={handleResolutionChange}
              handleResolutionTypeChange={handleResolutionTypeChange}
              isDisabled={isReadOnly}
              className="resolution-item"
              isFirstResolution={index === 0}
              py={0.5}
            />
            {index === resolutions.length - 1 && (
              <BetweenResolutionDropBorder
                side="bottom"
                resolutionIndex={index + 1}
                onReorderResolutions={handleReorderResolutions}
              />
            )}
          </ItemAndDropBorderContainer>
        ) : null,
      )}
      {/* Add a resolution */}
      <HStack
        align="stretch"
        alignItems="center"
        pt={0.5}
        spacing={1}
        className="resolution-add-container"
      >
        <IconButton size="md" isDisabled>
          <DragHandleIcon />
        </IconButton>
        <ResolutionTypeSelect
          options={resolutionTypeOptions}
          components={{
            Option: IconOption,
            Control: IconControl,
            GroupHeading: EmptyGroupHeading,
          }}
          onChange={(option) =>
            handleResolutionTypeChange(option.value, resolutions.length)
          }
          placeholder="Add a resolution"
          value=""
          className="resolution-type-select"
        />
      </HStack>
    </VStack>
  );
};
